import { DeviceTestResult } from "../results/results-view/ResultsView.types";
import {
  DeviceTesterTestState,
  DeviceInfo,
  DeviceSettings,
  AudioTestState,
  ResultsViewState,
  DeviceStatusDialogState,
  AudioDevices,
  CameraDevices,
} from "../../types/Device.types";

/**
 * State managed by DeviceTester component
 */
export type DeviceTesterState = {
  // Flag to determine whether device settings have been set
  isInfoFetched: boolean;

  // If we successfully fetched ACS token
  isAcsTokenFetched: boolean;

  // Collection of device related settings
  deviceSettings: DeviceSettings;

  // State of the device testing process
  deviceTesterTestState: DeviceTesterTestState;

  // State of Audio test
  audioTestState: AudioTestState;

  // State of Results View
  resultsViewState: ResultsViewState;

  // State of the Results Device Status Dialog
  deviceStatusDialogState: DeviceStatusDialogState;

  // Result of each device test
  microphoneResult: DeviceTestResult;
  speakerResult: DeviceTestResult;
  cameraResult: DeviceTestResult;
  networkResult: DeviceTestResult;
};

export enum DeviceTesterActionType {
  // Set device testing state
  SET_DEVICE_TEST_STATE = "SET_DEVICE_TEST_STATE",

  // Set device testing state
  SET_AUDIO_TEST_STATE = "SET_AUDIO_TEST_STATE",

  // Set results view state
  SET_RESULTS_VIEW_STATE = "SET_RESULTS_VIEW_STATE",

  // Set results status dialog box view state
  SET_DEVICE_STATUS_DIALOG_STATE = "SET_DEVICE_STATUS_DIALOG_STATE",

  // Set available device information
  SET_DEVICE_INFO = "SET_DEVICE_INFO",

  // Update available devices
  UPDATE_AUDIO_DEVICES = "UPDATE_AUDIO_DEVICES",
  UPDATE_VIDEO_DEVICES = "UPDATE_VIDEO_DEVICES",

  // Set device test results
  SET_CAMERA_RESULT = "SET_CAMERA_RESULT",
  SET_NETWORK_RESULT = "SET_NETWORK_RESULT",
  SET_MICROPHONE_RESULT = "SET_MICROPHONE_RESULT",
  SET_SPEAKER_RESULT = "SET_SPEAKER_RESULT",

  //  Toggle video streaming from camera
  SET_VIDEO_STREAMING = "SET_VIDEO_STREAMING",

  // Set devices for testing
  SET_SELECTED_SPEAKER = "SET_SELECTED_SPEAKER",
  SET_SELECTED_MICROPHONE = "SET_SELECTED_MICROPHONE",
  SET_SELECTED_CAMERA = "SET_SELECTED_CAMERA",

  // Handle resetting of device test results and transition to start screen
  RESET_DEVICE_TEST = "RESET_DEVICE_TEST",
}

export type DeviceTesterAction =
  | {
      type: DeviceTesterActionType.SET_DEVICE_TEST_STATE;
      state: DeviceTesterTestState;
    }
  | {
      type: DeviceTesterActionType.SET_AUDIO_TEST_STATE;
      audioTestState: AudioTestState;
    }
  | {
      type: DeviceTesterActionType.SET_RESULTS_VIEW_STATE;
      resultsViewState: ResultsViewState;
    }
  | {
      type: DeviceTesterActionType.SET_DEVICE_STATUS_DIALOG_STATE;
      deviceStatusDialogState: DeviceStatusDialogState;
    }
  | {
      type: DeviceTesterActionType.SET_DEVICE_INFO;
      deviceInfo: DeviceInfo;
    }
  | {
      type: DeviceTesterActionType.UPDATE_AUDIO_DEVICES;
      newAudioDevices: AudioDevices;
    }
  | {
      type: DeviceTesterActionType.UPDATE_VIDEO_DEVICES;
      newCameraDevices: CameraDevices;
    }
  | {
      type: DeviceTesterActionType.SET_CAMERA_RESULT;
      cameraResult: DeviceTestResult;
    }
  | {
      type: DeviceTesterActionType.SET_NETWORK_RESULT;
      networkResult: DeviceTestResult;
    }
  | {
      type: DeviceTesterActionType.SET_MICROPHONE_RESULT;
      microphoneResult: DeviceTestResult;
    }
  | {
      type: DeviceTesterActionType.SET_SPEAKER_RESULT;
      speakerResult: DeviceTestResult;
    }
  | { type: DeviceTesterActionType.SET_VIDEO_STREAMING; isCamOn: boolean }
  | { type: DeviceTesterActionType.SET_SELECTED_SPEAKER; speakerId: string }
  | {
      type: DeviceTesterActionType.SET_SELECTED_MICROPHONE;
      microphoneId: string;
    }
  | { type: DeviceTesterActionType.SET_SELECTED_CAMERA; cameraId: string }
  | { type: DeviceTesterActionType.RESET_DEVICE_TEST };
